import classnames from 'classnames';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import ThumbsDownIcon from '../assets/icons/ThumbsDown';
import ThumbsUpIcon from '../assets/icons/ThumbsUp';
import useFeedback from '../hooks/useFeedback';
import { Feedback } from '../services/FeedbackService';

export interface RatingDetailedProps {
	feedback: Feedback;
	shakeLabel?: boolean;
	isReadOnly?: boolean;
}

// https://stackoverflow.com/a/45677919/5243574
export const RatingDetailed: React.FC<
	RatingDetailedProps & React.HTMLProps<HTMLFieldSetElement>
> = ({ feedback, shakeLabel, isReadOnly, className, ...props }) => {
	const upvoteRef = React.useRef<HTMLInputElement>(null);
	const downvoteRef = React.useRef<HTMLInputElement>(null);
	const [chosenRating, setChosenRating] = React.useState(feedback.rating);
	const [showThumbs, setShowThumbs] = React.useState(true);
	const [allowEditFeedback, setAllowEditFeedback] = React.useState(false);
	const { saveFeedback, createFeedbackForm } = useFeedback();
	const { t } = useTranslation();

	async function handleRatingChange(evt: React.ChangeEvent<HTMLInputElement>) {
		const value = evt.target.checked ? parseInt(evt.target.value, 10) : 0;
		const target = evt.target;

		setChosenRating(value);

		try {
			saveFeedback(
				{
					...feedback,
					rating: value,
				},
				'rating'
			);
		} catch (error) {
			// TODO: Log errors in cloudwatch?
			// TODO: Show error message?
		}

		if (upvoteRef && upvoteRef.current && downvoteRef && downvoteRef.current) {
			if (evt.target.checked) {
				// Uncheck the opposite vote
				if (target === upvoteRef.current) {
					downvoteRef.current.checked = false;
				} else {
					upvoteRef.current.checked = false;
				}
			} else {
				downvoteRef.current.checked = false;
				upvoteRef.current.checked = false;
			}
		}
	}

	function handleShareFeedback() {
		setAllowEditFeedback(false);

		createFeedbackForm(feedback);
	}

	React.useEffect(() => {
		let shouldShowThumbs = true;

		if (feedback.comments && feedback.comments.length) {
			shouldShowThumbs = false;
		}

		if (allowEditFeedback) {
			shouldShowThumbs = true;
		}

		setShowThumbs(shouldShowThumbs);
	}, [allowEditFeedback, feedback]);

	return (
		<fieldset
			data-testid={`rating-${feedback.messageId}`}
			className={className}
			{...props}
		>
			<div className="flex items-center">
				<div className="text-caption flex-grow">
					<legend
						className={classnames({
							'sr-only': chosenRating && chosenRating !== 0,
							'animate-shake-x': shakeLabel,
						})}
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{ __html: t('ratingDetailed:question') }}
					></legend>
					<div
						className={classnames({
							'invisible h-0 overflow-hidden': chosenRating !== 1, // Occupy width (not height) in the DOM
						})}
					>
						{t('ratingDetailed:acknowledgementPositive')}
					</div>
					<div
						className={classnames({
							'invisible h-0 overflow-hidden': chosenRating !== -1, // Occupy width (not height) in the DOM
						})}
					>
						{t('ratingDetailed:acknowledgementNegative')}
					</div>

					{feedback.rating !== 0 && (
						<div data-testid="edit-buttons">
							{/* TODO: Should be based on the existence of score buttons too (not just comments) */}
							{!feedback.comments ? (
								<button
									data-testid="rating-share-feedback-cta"
									data-tag-name="open feedback form"
									data-tag-feedback-id={feedback.id}
									data-tag-subject-type={feedback.subjectType}
									data-tag-subject-name={feedback.subjectName}
									type="button"
									className="text-link p-0 text-xs bg-white cursor-pointer"
									onClick={handleShareFeedback}
								>
									{t('ratingDetailed:feedbackFormCtaShare')}
								</button>
							) : (
								<React.Fragment>
									{allowEditFeedback ? (
										<button
											data-testid="rating-my-feedback-cta"
											data-tag-name="open feedback form"
											data-tag-feedback-id={feedback.id}
											data-tag-subject-type={feedback.subjectType}
											data-tag-subject-name={feedback.subjectName}
											type="button"
											className="text-link p-0 text-xs bg-white cursor-pointer"
											onClick={handleShareFeedback}
										>
											{t('ratingDetailed:feedbackFormCtaAmend')}
										</button>
									) : (
										<button
											data-testid="rating-edit-feedback-cta-"
											data-tag-name="edit feedback"
											data-tag-feedback-id={feedback.id}
											data-tag-subject-type={feedback.subjectType}
											data-tag-subject-name={feedback.subjectName}
											type="button"
											className="text-link p-0 text-xs bg-white cursor-pointer"
											onClick={() => setAllowEditFeedback(true)}
										>
											{t('ratingDetailed:feedbackFormCtaEdit')}
										</button>
									)}
								</React.Fragment>
							)}
						</div>
					)}
				</div>
				<div
					ref={(node) => node && isReadOnly && node.setAttribute('inert', '')}
					data-testid="rating-buttons"
					className={classnames('flex', {
						invisible: !showThumbs, // Preserve the height of the voting thumbs but don't show them
					})}
					aria-hidden={showThumbs ? undefined : 'true'}
				>
					<label
						data-testid="rating-label-upvote"
						htmlFor={`rating-upvote-${feedback.messageId}`}
						className={classnames(
							'c-chatbot-rating-label flex items-center justify-center border border-solid rounded-full cursor-pointer',
							{
								'bg-ongreen border-ongreen text-white': chosenRating === 1,
								'text-cta-action border-gray-40 hover:bg-gray-20':
									chosenRating !== 1,
							}
						)}
						style={{ width: '44px', height: '44px' }}
					>
						<input
							data-testid="rating-checkbox-upvote"
							className="sr-only"
							ref={upvoteRef}
							id={`rating-upvote-${feedback.messageId}`}
							name="rating-upvote"
							type="checkbox"
							value="1"
							onChange={handleRatingChange}
							defaultChecked={
								typeof feedback.rating !== 'undefined' && feedback.rating === 1
							}
						/>
						<span className="sr-only">
							{t('ratingDetailed:voteLabelThumbsUp')}
						</span>
						<ThumbsUpIcon style={{ fontSize: '16px' }} />
					</label>
					<label
						data-testid="rating-label-downvote"
						htmlFor={`rating-downvote-${feedback.messageId}`}
						className={classnames(
							'c-chatbot-rating-label flex items-center justify-center border border-solid rounded-full cursor-pointer ml-1',
							{
								'bg-deals border-deals text-white': chosenRating === -1, // TODO: Change bg color to #b50016 once available in Strata
								'text-cta-action border-gray-40 hover:bg-gray-20':
									chosenRating !== -1,
							}
						)}
						style={{ width: '44px', height: '44px' }}
					>
						<input
							data-testid="rating-checkbox-downvote"
							className="sr-only"
							ref={downvoteRef}
							id={`rating-downvote-${feedback.messageId}`}
							name="rating-downvote"
							type="checkbox"
							value="-1"
							onChange={handleRatingChange}
							defaultChecked={
								typeof feedback.rating !== 'undefined' && feedback.rating === -1
							}
						/>
						<span className="sr-only">
							{t('ratingDetailed:voteLabelThumbsDown')}
						</span>
						<ThumbsDownIcon style={{ fontSize: '16px' }} />
					</label>
				</div>
			</div>
		</fieldset>
	);
};

export default RatingDetailed;
